import {
  Button,
  IconButton,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Tooltip,
} from '@ads/components-react';
import { InformationCircleIcon, PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { EClassType } from '@/shared/api/generated';

import { useClassModuleForm } from '../../../context/ClassModuleFormContext';
import type {
  ClassModuleDisciplineForm,
  ClassModuleRecurrenceForm,
} from '../../../types/classModuleForm';
import {
  ClassRoomAutocomplete,
  DisciplineAutocomplete,
  TeacherAutocomplete,
} from '../../molecules/LookupAutocomplete';
import { RecurrenceModal } from './RecurrenceModal';

interface DisciplineDraft {
  classroomId: number | null;
  classroomName: string;
  disciplineId: number | null;
  disciplineName: string;
  teacherPersonId: number | null;
  teacherName: string;
  quantity: string;
  type: EClassType;
  planningWithExam: boolean;
  useMoveQA: boolean;
  isVisualizationAttendance: boolean;
  recurrences: ClassModuleRecurrenceForm[];
}

const EMPTY_DRAFT: DisciplineDraft = {
  classroomId: null,
  classroomName: '',
  disciplineId: null,
  disciplineName: '',
  teacherPersonId: null,
  teacherName: '',
  quantity: '',
  type: 1,
  planningWithExam: true,
  useMoveQA: true,
  isVisualizationAttendance: false,
  recurrences: [],
};

const REC_GRID = 'grid grid-cols-[1.3fr_1.6fr_1.3fr_3.5rem] items-center gap-micro';

/** Card de configuração: label (com info opcional) à esquerda, toggle à direita. */
function SwitchRow({
  label,
  checked,
  onCheckedChange,
  info,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly onCheckedChange: (checked: boolean) => void;
  readonly info?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-micro rounded-sm border-hairline border-ctx-layout-border px-mini py-micro">
      <span className="flex items-center gap-quark text-ctx-content-title ts-paragraph-xxs">
        {label}
        {info && (
          <Tooltip custom={{ root: 'z-[9999]' }} title={info}>
            <InformationCircleIcon className="size-xxxs shrink-0 text-ctx-content-base" />
          </Tooltip>
        )}
      </span>
      <Switch checked={checked} size="sm" onCheckedChange={onCheckedChange} />
    </div>
  );
}

/**
 * Modal de cadastro/edição de disciplina do módulo. Opera sobre um rascunho
 * local (sala, disciplina, professor, quantidade e recorrências) e, ao salvar,
 * comita no field array de disciplinas via contexto. Exclusão de recorrência é
 * confirmada por modal.
 */
export function DisciplineFormModal() {
  const { t } = useTranslation('classModuleManagement');
  const {
    disciplines,
    disciplineModalIndex,
    isDisciplineModalOpen,
    closeDisciplineModal,
    saveDiscipline,
  } = useClassModuleForm();

  const [draft, setDraft] = useState<DisciplineDraft>(EMPTY_DRAFT);
  const [seededLessons, setSeededLessons] = useState<ClassModuleDisciplineForm['lessons']>([]);
  const [showErrors, setShowErrors] = useState(false);
  const [isRecurrenceOpen, setRecurrenceOpen] = useState(false);

  const isEditing = disciplineModalIndex !== null;

  useEffect(() => {
    if (!isDisciplineModalOpen) return;
    setShowErrors(false);
    if (disciplineModalIndex === null) {
      setDraft(EMPTY_DRAFT);
      setSeededLessons([]);
      return;
    }
    const source = disciplines[disciplineModalIndex];
    if (!source) return;
    setDraft({
      classroomId: source.classroomId,
      classroomName: source.classroomName,
      disciplineId: source.disciplineId || null,
      disciplineName: source.disciplineName,
      teacherPersonId: source.teacherPersonId,
      teacherName: source.teacherName,
      quantity: String(source.quantity ?? ''),
      type: source.type,
      planningWithExam: source.planningWithExam,
      useMoveQA: source.useMoveQA,
      isVisualizationAttendance: source.isVisualizationAttendance,
      recurrences: [...source.recurrences],
    });
    setSeededLessons(source.lessons);
  }, [isDisciplineModalOpen, disciplineModalIndex, disciplines]);

  const disciplineInvalid = showErrors && draft.disciplineId === null;
  const quantityInvalid = showErrors && (draft.quantity === '' || Number(draft.quantity) <= 0);

  const handleSave = () => {
    if (draft.disciplineId === null || draft.quantity === '' || Number(draft.quantity) <= 0) {
      setShowErrors(true);
      return;
    }
    const item: ClassModuleDisciplineForm = {
      disciplineId: draft.disciplineId,
      disciplineName: draft.disciplineName,
      classroomId: draft.classroomId,
      classroomName: draft.classroomName,
      teacherPersonId: draft.teacherPersonId,
      teacherName: draft.teacherName,
      quantity: Number(draft.quantity),
      type: draft.type,
      planningWithExam: draft.planningWithExam,
      useMoveQA: draft.useMoveQA,
      isVisualizationAttendance: draft.isVisualizationAttendance,
      recurrences: draft.recurrences,
      lessons: seededLessons,
    };
    saveDiscipline(item, disciplineModalIndex);
  };

  return (
    <Dialog.Root
      open={isDisciplineModalOpen}
      onOpenChange={open => !open && closeDisciplineModal()}
      modal={false}
    >
      <Dialog.Portal>
        {/* Backdrop manual: com `modal={false}` o Radix não renderiza o Dialog.Overlay
            nem aplica `pointer-events: none` no body — necessário para que o dropdown do
            Autocomplete (portado para o body) receba cliques. */}
        <div
          aria-hidden="true"
          className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft"
        />
        <Dialog.Content
          aria-describedby={undefined}
          // Não fechar por interação externa: cliques no dropdown do Autocomplete
          // (portado para o body) contam como "fora" e disparariam o dismiss.
          onInteractOutside={e => e.preventDefault()}
          className="-translate-x-1/2 -translate-y-1/2 fixed top-1/2 left-1/2 z-50 flex max-h-[90vh] w-full max-w-[45rem] flex-col rounded-md bg-ctx-layout-surface shadow-level4 focus:outline-none"
        >
          <div className="flex items-center justify-between border-ctx-layout-border border-b-hairline px-xxs py-xxxs">
            <Dialog.Title className="text-ctx-content-title ts-heading-sm">
              {isEditing ? t('disciplineModal.editTitle') : t('disciplineModal.addTitle')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('disciplineModal.cancel')}
              />
            </Dialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto px-xxs py-xxs at-scrollbar">
            <div className="grid grid-cols-1 gap-mini sm:grid-cols-2">
              <ClassRoomAutocomplete
                label={t('disciplineModal.classroom')}
                placeholder={t('disciplineModal.classroomPlaceholder')}
                value={draft.classroomId}
                selectedLabel={draft.classroomName}
                onChange={(id, label) =>
                  setDraft(prev => ({
                    ...prev,
                    classroomId: id,
                    classroomName: label,
                  }))
                }
              />
              <DisciplineAutocomplete
                label={`${t('disciplineModal.discipline')}*`}
                placeholder={t('disciplineModal.disciplinePlaceholder')}
                value={draft.disciplineId}
                selectedLabel={draft.disciplineName}
                hasError={disciplineInvalid}
                onChange={(id, label) =>
                  setDraft(prev => ({
                    ...prev,
                    disciplineId: id,
                    disciplineName: label,
                  }))
                }
              />
              <TeacherAutocomplete
                label={t('disciplineModal.teacher')}
                placeholder={t('disciplineModal.teacherPlaceholder')}
                value={draft.teacherPersonId}
                selectedLabel={draft.teacherName}
                onChange={(id, label) =>
                  setDraft(prev => ({
                    ...prev,
                    teacherPersonId: id,
                    teacherName: label,
                  }))
                }
              />
              <TextField
                label={`${t('disciplineModal.quantity')}*`}
                type="number"
                placeholder={t('disciplineModal.quantity')}
                value={draft.quantity}
                onChange={e => setDraft(prev => ({ ...prev, quantity: e.target.value }))}
                size="md"
                hasError={quantityInvalid}
                {...(quantityInvalid ? { helper: t('disciplineModal.required') } : {})}
              />
            </div>

            <fieldset className="mt-xxs">
              <legend className="mb-nano text-ctx-content-title ts-heading-xxs">
                {`${t('disciplineModal.type.label')}*`}
              </legend>
              <RadioGroup
                value={String(draft.type)}
                onValueChange={v => setDraft(prev => ({ ...prev, type: Number(v) as EClassType }))}
                className="flex gap-mini"
              >
                <Radio value="1" label={t('disciplineModal.type.options.normal')} size="sm" />
                <Radio value="2" label={t('disciplineModal.type.options.onDemand')} size="sm" />
              </RadioGroup>
            </fieldset>

            <div className="mt-xxs border-ctx-layout-border border-b-hairline pb-nano">
              <h3 className="text-ctx-content-title ts-heading-xs">
                {t('disciplineModal.settings.title')}
              </h3>
            </div>
            <div className="mt-mini grid grid-cols-1 gap-mini sm:grid-cols-2">
              <SwitchRow
                label={t('disciplineModal.settings.planningWithExam')}
                checked={draft.planningWithExam}
                onCheckedChange={checked =>
                  setDraft(prev => ({ ...prev, planningWithExam: checked }))
                }
              />
              <SwitchRow
                label={t('disciplineModal.settings.useMoveQA')}
                info={t('disciplineModal.settings.useMoveQAInfo')}
                checked={draft.useMoveQA}
                onCheckedChange={checked => setDraft(prev => ({ ...prev, useMoveQA: checked }))}
              />
              <SwitchRow
                label={t('disciplineModal.settings.isVisualizationAttendance')}
                checked={draft.isVisualizationAttendance}
                info={t('disciplineModal.settings.isVisualizationAttendanceInfo')}
                onCheckedChange={checked =>
                  setDraft(prev => ({ ...prev, isVisualizationAttendance: checked }))
                }
              />
            </div>

            <div className="mt-xxs flex items-center justify-between border-ctx-layout-border border-b-hairline pb-nano">
              <h3 className="text-ctx-content-title ts-heading-xs">
                {t('disciplineModal.recurrence.title')}
              </h3>
              <Button
                hierarchy="secondary"
                size="sm"
                leadingIcon={PlusIcon}
                onClick={() => setRecurrenceOpen(true)}
              >
                {t('disciplineModal.recurrence.add')}
              </Button>
            </div>

            <div className="mt-mini overflow-hidden rounded-sm border-hairline border-ctx-layout-border">
              <div
                className={`${REC_GRID} bg-ctx-layout-body px-mini py-micro text-ctx-content-base ts-heading-xxxs`}
              >
                <span>{t('disciplineModal.recurrence.columns.type')}</span>
                <span>{t('disciplineModal.recurrence.columns.day')}</span>
                <span>{t('disciplineModal.recurrence.columns.time')}</span>
                <span />
              </div>
              {draft.recurrences.length === 0 ? (
                <p className="px-mini py-xxxs text-center text-ctx-content-placeholder ts-paragraph-xs">
                  {t('disciplineModal.recurrence.empty')}
                </p>
              ) : (
                draft.recurrences.map((recurrence, index) => (
                  <div
                    key={index}
                    className={`${REC_GRID} border-ctx-layout-border border-t-hairline px-mini py-micro text-ctx-content-title ts-paragraph-xs`}
                  >
                    <span>
                      {recurrence.type === 2
                        ? t('recurrenceModal.repeatOptions.monthly')
                        : t('recurrenceModal.repeatOptions.weekly')}
                    </span>
                    <span className="text-ctx-content-base">
                      {recurrence.type === 2
                        ? t('recurrenceModal.monthDayLabel', {
                            day: recurrence.monthDay ?? '',
                          })
                        : t(
                            `recurrenceModal.weekDays.${recurrence.weekDay ?? 0}` as 'recurrenceModal.weekDays.0',
                          )}
                    </span>
                    <span className="text-ctx-content-base">
                      {recurrence.startTime} - {recurrence.endTime}
                    </span>
                    <IconButton
                      icon={TrashIcon}
                      hierarchy="tertiary"
                      size="sm"
                      ariaLabel={t('disciplineModal.recurrence.remove')}
                      onClick={() =>
                        setDraft(prev => ({
                          ...prev,
                          recurrences: prev.recurrences.filter((_, i) => i !== index),
                        }))
                      }
                    />
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="flex justify-end gap-micro border-ctx-layout-border border-t-hairline px-xxs py-xxxs">
            <Dialog.Close asChild>
              <Button hierarchy="secondary" size="md">
                {t('disciplineModal.cancel')}
              </Button>
            </Dialog.Close>
            <Button hierarchy="primary" size="md" onClick={handleSave}>
              {isEditing ? t('disciplineModal.save') : t('disciplineModal.confirmAdd')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>

      <RecurrenceModal
        open={isRecurrenceOpen}
        onOpenChange={setRecurrenceOpen}
        onAdd={recurrence =>
          setDraft(prev => ({
            ...prev,
            recurrences: [...prev.recurrences, recurrence],
          }))
        }
      />
    </Dialog.Root>
  );
}
